/**
 * Helpers for TanStack columnOrder / column selector: persist merge, subset
 * reorder, locked-column visibility, and selector-row mapping.
 */

export function columnIdFromDef(column: {
  id?: string;
  accessorKey?: string | number | symbol;
}): string {
  if (column.id) return column.id;
  if (column.accessorKey != null) return String(column.accessorKey);
  return "";
}

export function columnIdsFromDefs(
  columns: ReadonlyArray<{
    id?: string;
    accessorKey?: string | number | symbol;
  }>,
): string[] {
  return columns.map(columnIdFromDef).filter(Boolean);
}

export type SelectorLeafColumn = {
  id: string;
  columnDef: { header?: unknown };
  getCanHide: () => boolean;
  getIsVisible: () => boolean;
  getCanSort?: () => boolean;
  getIsSorted?: () => false | "asc" | "desc";
};

export type SelectorColumn = {
  id: string;
  label: string;
  isVisible: boolean;
  canHide: boolean;
  canReorder: boolean;
  alwaysVisible: boolean;
  canSort: boolean;
  sortDirection: false | "asc" | "desc";
};

export type ColumnSortDirection = false | "asc" | "desc";

/** Next direction in the header cycle: unsorted → asc → desc → unsorted. */
export function nextColumnSortDirection(
  current: ColumnSortDirection,
): ColumnSortDirection {
  if (current === false) return "asc";
  if (current === "asc") return "desc";
  return false;
}

/** Apply an explicit sort direction (unsorted → asc → desc → unsorted). */
export function applyColumnSortDirection(
  column:
    | {
        toggleSorting?: (_desc?: boolean, _isMulti?: boolean) => void;
        clearSorting?: () => void;
      }
    | undefined,
  direction: ColumnSortDirection,
): void {
  if (!column) return;
  if (direction === false) {
    column.clearSorting?.();
    return;
  }
  column.toggleSorting?.(direction === "desc");
}

/** True when the table instance was given a controlled `columnOrder` in options.state. */
export function isColumnOrderStateControlled(
  state: { columnOrder?: unknown } | undefined,
): boolean {
  return state != null && Object.hasOwn(state, "columnOrder");
}

/**
 * Force the first leaf and `enableHiding: false` columns to visible so stale
 * storage cannot hide locked columns. `actions` is left unchanged.
 */
export function forceLockedColumnsVisible(
  visibility: Record<string, boolean>,
  columns: ReadonlyArray<{
    id?: string;
    accessorKey?: string | number | symbol;
    enableHiding?: boolean;
  }>,
): Record<string, boolean> {
  const next = { ...visibility };
  const firstId = columnIdsFromDefs(columns)[0];
  if (firstId && firstId !== "actions") {
    next[firstId] = true;
  }
  for (const col of columns) {
    const id = columnIdFromDef(col);
    if (!id || id === "actions") continue;
    if (col.enableHiding === false) {
      next[id] = true;
    }
  }
  return next;
}

/**
 * Map a TanStack leaf column to Column Visibility menu state.
 * Locked: first leaf, `actions`, `!getCanHide()`, or `extraLocked`.
 */
export function toSelectorColumn(
  column: SelectorLeafColumn,
  firstLeafId: string | undefined,
  overrides?: {
    canHide?: boolean;
    canReorder?: boolean;
    extraLocked?: boolean;
  },
): SelectorColumn {
  const locked =
    column.id === firstLeafId ||
    column.id === "actions" ||
    !column.getCanHide() ||
    overrides?.extraLocked === true;
  const canHide = overrides?.canHide ?? !locked;
  const canReorder = overrides?.canReorder ?? !locked;
  return {
    id: column.id,
    label:
      typeof column.columnDef.header === "string"
        ? column.columnDef.header
        : column.id,
    isVisible: locked ? true : column.getIsVisible(),
    canHide,
    canReorder,
    alwaysVisible: locked,
    canSort: column.getCanSort?.() ?? false,
    sortDirection: column.getIsSorted?.() ?? false,
  };
}

/**
 * Keep stored order for ids that still exist, then append any new column ids.
 */
export function mergeColumnOrder(
  stored: string[],
  currentIds: string[],
): string[] {
  const currentSet = new Set(currentIds);
  const kept = stored.filter((id) => currentSet.has(id));
  const keptSet = new Set(kept);
  return [...kept, ...currentIds.filter((id) => !keptSet.has(id))];
}

/** Parse `{storageKey}-column-order` JSON; invalid/missing → `[]`. */
export function parseStoredColumnOrder(
  stored: string | null,
  currentIds: string[],
): string[] {
  if (!stored) return [];
  try {
    const parsed: unknown = JSON.parse(stored);
    if (Array.isArray(parsed) && parsed.every((id) => typeof id === "string")) {
      return mergeColumnOrder(parsed, currentIds);
    }
  } catch {
    // Fall through to default (definition order)
  }
  return [];
}

/**
 * Replace movable ids in `fullOrder` with `movableIds` (same count/slots).
 * Ids not in the movable set keep their positions (sticky / unhideable).
 */
export function applyMovableOrder(
  fullOrder: string[],
  movableIds: string[],
): string[] {
  const movable = new Set(movableIds);
  const queue = [...movableIds];
  return fullOrder.map((id) => {
    if (!movable.has(id)) return id;
    return queue.shift() ?? id;
  });
}
